import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SWAP_COMMISSION } from '../constants/business'
import { CKBTC_ICP_ID, ODINBTC_ID } from '../constants/tokenIds'
import { GAEventName } from '../interfaces/googleAnalytics'
import { SwapProvider, SwapProviderQuote } from '../interfaces/swapInterface'
import { logger } from '../logger'
import { useGoogleAnalytics } from '../providers/GoogleAnalyticsProvider'
import { useSwapContext } from '../providers/SwapProvider'
import { useCurrentWallet, useSwapSlippage, useUserTokens } from '../state'
import { formatNumber } from '../utils/formatNumber'
import { e8sToDecimals } from '../utils/validationHelper'
import useBalances from './useBalances'
import { PriceDifference } from './useSwap'

interface Props {
  performSwap: (quote: SwapProviderQuote) => Promise<number>
  onError: (error: string) => void
  close: () => void
  formattedFee?: string[]
  priceDifference?: PriceDifference
}

const SUCCESS_TIMEOUT = 3000

const useSwapReview = ({ performSwap, onError, close, formattedFee, priceDifference }: Props) => {
  const { t } = useTranslation()
  const { refreshBalance } = useBalances()
  const { currentWallet } = useCurrentWallet()
  const { addToken } = useUserTokens()
  const {
    fromToken,
    toToken,
    amount,
    selectedProvider,
    quotes,
    setSwapReviewVisible,
    swapLoading,
    setSwapLoading,
  } = useSwapContext()

  const [userApprovedQuote, setUserApprovedQuote] = useState<SwapProviderQuote>()
  const [userApprovedFee, setUserApprovedFee] = useState<string[]>()
  const [swapSuccess, setSwapSuccess] = useState(false)
  const { slippage } = useSwapSlippage()
  const { fireEvent } = useGoogleAnalytics()

  const quote = userApprovedQuote || quotes.find((q) => q.provider === selectedProvider)
  const fee = userApprovedFee || formattedFee

  useEffect(() => {
    if (!quotes.length && !swapLoading && !swapSuccess) {
      setSwapReviewVisible(false)
    }
  }, [quotes, swapLoading, swapSuccess])

  const swapDetails = useMemo(() => {
    return [
      quote?.provider === SwapProvider.STEALTHEX && {
        title: 'Receive delay',
        value: 'Up to 2 hours',
        info: 'Token withdrawal can take up to 2 hours when swapping with StealthEX. The delay depends on tokens being swapped though and usually the delay is much lower.',
      },
      quote?.provider === SwapProvider.DFINITY && {
        title: 'Receive delay',
        value: 'Up to 1 hour',
        info: 'Token withdrawal can take up to 1 hour when minting via Dfinity. The delay depends on tokens being swapped though and usually the delay is much lower.',
      },
      ...(fee?.map((f) => ({
        title: 'Network fee',
        value: f,
      })) ?? []),
      {
        title: 'Commission',
        value: `${quote?.provider === SwapProvider.DFINITY ? 0 : SWAP_COMMISSION * 100}%`,
      },
      typeof quote?.slippage === 'number'
        ? {
            title: 'Slippage',
            value: formatNumber(quote.slippage, '%', { maximumFractionDigits: 2 }),
          }
        : {
            title: 'Max slippage',
            value: `${slippage}%`,
            adjustable: true,
          },
      priceDifference && {
        title: 'Price Difference',
        value: formatNumber(priceDifference.percentage, '%', {
          maximumFractionDigits: 2,
          signDisplay: 'exceptZero',
        }),
      },
    ].filter((d) => !!d)
  }, [quote, fee, slippage])

  const doSwap = async () => {
    if (!quote) return

    setUserApprovedQuote(quote)
    setUserApprovedFee(formattedFee)
    setSwapLoading(true)

    try {
      const received = await performSwap(quote)

      if (selectedProvider == 'Dfinity') {
        fireEvent(GAEventName.MINT, {
          token: `${toToken!.symbol}_${toToken!.chain}`,
          amount: Number(e8sToDecimals(amount, toToken!.decimals)),
        })
      } else if (selectedProvider === 'OneSec') {
        fireEvent(GAEventName.BRIDGE_ONESEC, {
          token: `${toToken!.symbol}_${toToken!.chain}`,
          amount: Number(e8sToDecimals(amount, toToken!.decimals)),
        })
      } else if (
        selectedProvider === 'Odin' &&
        toToken!.id === CKBTC_ICP_ID &&
        fromToken!.id === ODINBTC_ID
      ) {
        fireEvent(GAEventName.WITHDRAW_ODIN, {
          amount: Number(e8sToDecimals(amount, fromToken!.decimals)),
        })
      } else if (
        selectedProvider === 'Odin' &&
        toToken!.id === ODINBTC_ID &&
        fromToken!.id === CKBTC_ICP_ID
      ) {
        fireEvent(GAEventName.DEPOSIT_ODIN, {
          amount: Number(e8sToDecimals(amount, fromToken!.decimals)),
        })
      } else {
        fireEvent(GAEventName.SWAP, {
          type: 'swap',
          provider: quote.provider,
          from_token: fromToken!.symbol,
          to_token: toToken!.symbol,
          amount: Number(amount),
          amount_received: received / 10 ** toToken!.decimals,
        })
      }

      setSwapSuccess(true)

      addToken(currentWallet, toToken!.id)

      setTimeout(() => {
        refreshBalance(fromToken!)
        refreshBalance(toToken!)
        setSwapReviewVisible(false)
        close()
      }, SUCCESS_TIMEOUT)
    } catch (e) {
      logger.error('Swap failed', { originalError: e, fromToken, toToken })

      let humanError
      const errorMessage = typeof e === 'string' ? e : (e as Error).message

      if (errorMessage.includes('this transaction exceeds the balance of the account')) {
        humanError = t('swap.errors.not_enough_for_gas')
      } else if (errorMessage.length > 200) {
        humanError = t('swap.errors.unknown_error')
      } else {
        humanError = errorMessage
      }

      onError(humanError)
    }

    setSwapLoading(false)
    setUserApprovedQuote(undefined)
  }

  return {
    userApprovedQuote,
    swapLoading,
    swapSuccess,
    swapDetails,
    doSwap,
  }
}

export default useSwapReview
